<script lang="ts">
import { computed, provide } from 'vue';

import { PhxsgIconIds, PhxsgIcon } from '@sg/src/View/Components/Atoms/Icon';
import { PhxsgButtonProps, PhxsgButton as PhxsgCoreButton } from '@sg-core/src/View/Components/Atoms/Button';
import { PhxsgLoaderProps } from '@sg-core/src/View/Components/Atoms/Loader';
import '@sg/src/View/Components/Atoms/Loader/loader.css';

export type IwcButtonColor = 'primary' | 'secondary' | 'tertiary';
export type IwcButtonVariant = 'plain' | 'fade' | 'ghost' | 'tag';

export type IwcPhxsgButtonProps = Omit<PhxsgButtonProps, 'variant'> & {
  color?: IwcButtonColor;
  variant?: IwcButtonVariant;
  noCaps?: boolean;
  /**
   * The `iconId` prop is used to render an icon inside the button, either as the start icon
   * or the "icon-only" element in the `content` slot.
   *
   * It accepts a string value that corresponds to the icon id.
   *
   * To have more control over the icon placement, you can use the `icon` prop
   * instead with a `start` or `end` property. The `icon` prop takes precedence over
   * `iconId` if both are provided.
   */
  iconId?: PhxsgIconIds;
};
</script>

<script setup lang="ts">
const { color, variant, noCaps = false, iconId, icon, iconOnly, ...buttonProps } = defineProps<IwcPhxsgButtonProps>();

const buttonClasses = computed(() => ({
  ...(variant === 'plain' && { [`phxsg-btn--${color}`]: color }),
  'phxsg-btn--no-caps': noCaps,
}));

const btnIcon = computed(() => (iconId ? { id: iconId } : null));
const computedIcon = computed(() => {
  const iconValue = icon || (btnIcon.value && !iconOnly ? { start: btnIcon.value } : null);
  return iconValue ?? undefined;
});
const computedLoaderProps = computed((): PhxsgLoaderProps => ({
  type: 'line',
  ...(buttonProps.loaderProps ?? {}),
}));

provide('PhxsgIcon', PhxsgIcon);
</script>

<template>
  <PhxsgCoreButton
    v-bind="buttonProps"
    :loader-props="computedLoaderProps"
    :icon-only="iconOnly"
    :icon="computedIcon"
    :class="buttonClasses"
    :variant="variant"
  >
    <slot />

    <template v-if="$slots.content || iconOnly" #content>
      <slot name="content">
        <PhxsgIcon v-if="iconOnly && btnIcon" v-bind="btnIcon" />
      </slot>
    </template>
  </PhxsgCoreButton>
</template>

<style src="./button.css" />
